<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue'
  import { RouterLink } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { HLAvatar, HLBreadcrumb, HLButton, HLIcon, HLPopover, HLText } from '@platform-ui/highrise'
  import { useAuthStore } from '@/stores/auth'
  import { useTopBarStore } from '@/stores/topBar'
  import { useNotificationsStore } from '@/stores/notifications'
  import NotificationsDrawer from '@/components/NotificationsDrawer.vue'
  import { ChevronDownIcon } from '@gohighlevel/ghl-icons/24/outline'
  import { MaterialNotificationsIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

  const authStore = useAuthStore()
  const topBarStore = useTopBarStore()
  const notificationsStore = useNotificationsStore()
  const { breadcrumbs } = storeToRefs(topBarStore)
  const { unreadCount } = storeToRefs(notificationsStore)

  const isOpen = ref(false)
  const notificationsDrawerOpen = ref(false)

  const handleLogout = () => {
    authStore.logout()
  }

  onMounted(() => {
    // Seed the unread badge via the count-only endpoint (one cheap query, no list
    // or populate) — the drawer fetches the full list when opened.
    notificationsStore.fetchUnreadCount()
    // Open the realtime SSE stream so new notifications update the bell live.
    notificationsStore.connect()
  })

  onUnmounted(() => {
    // Close the SSE connection when the top bar tears down (e.g. logout redirect).
    notificationsStore.disconnect()
  })
</script>

<template>
  <div class="kaizen-top-bar">
    <div class="top-bar-left">
      <!-- <HLSelect
        id="search-bar"
        @search="setSearchString"
        :options="searchResults"
        :placeholder="searchPlaceholder"
        :filterable="true"
        :show-search-icon="true"
        size="sm"
        @update:value="handleValue"
      /> -->
      <HLBreadcrumb v-if="breadcrumbs.length > 0" :breadcrumbs="breadcrumbs" :home="true" @home-click="topBarStore.onBreadcrumbClick({ label: 'Home', href: '/' })" />
    </div>
    <slot name="tabs" />
    <div class="top-bar-right">
      <div class="notification-bell">
        <HLButton id="notifications-bell-button" variant="text" color="gray" size="xs" aria-label="Notifications" @click="notificationsDrawerOpen = true">
          <HLIcon size="24">
            <MaterialNotificationsIcon />
          </HLIcon>
        </HLButton>
        <span v-if="unreadCount > 0" class="notification-badge" :aria-label="`${unreadCount} unread notifications`">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </div>

      <HLPopover id="user-menu-popover" trigger="click" @update:show="isOpen = $event">
        <template #trigger>
          <HLButton id="user-menu-button" variant="text" color="gray" size="xs">
            <div class="flex items-center gap-2">
              <HLAvatar size="xs" :name="authStore.user?.name || 'User'" color="primary" />

              <HLText style="text-transform: capitalize">
                {{ authStore.user?.name || 'User' }}
              </HLText>
              <HLIcon size="16" :class="{ 'rotate-180': isOpen }">
                <ChevronDownIcon />
              </HLIcon>
            </div>
          </HLButton>
        </template>
        <div class="p-3">
          <div class="user-info">
            <HLAvatar size="xs" :name="authStore.user?.name || 'User'" color="primary" />
            <div>
              <HLText style="text-transform: capitalize">
                {{ authStore.user?.name }}
              </HLText>
              <HLText>
                {{ authStore.user?.email }}
              </HLText>
            </div>
          </div>
          <div class="user-menu-options">
            <RouterLink to="/profile" class="user-menu-option">Profile Settings</RouterLink>
            <span class="user-menu-option" @click="handleLogout">Logout</span>
          </div>
        </div>
      </HLPopover>
    </div>

    <NotificationsDrawer v-model:show="notificationsDrawerOpen" />
  </div>
</template>

<style scoped>
  .kaizen-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
  }
  .kaizen-top-bar .top-bar-right {
    display: flex;
    align-items: center;
    gap: 24px;
  }
  .notification-bell {
    position: relative;
    display: inline-flex;
    align-items: center;
  }
  .notification-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 12px;
    height: 12px;
    padding: 0px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background-color: var(--primary-500);
    color: #fff;
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 0 0 2px var(--secondary-25);
    pointer-events: none;
  }
  .top-bar-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .top-bar-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .top-bar-title {
    font-weight: 600;
  }
  .user-info {
    align-items: center;
    display: flex;
    gap: 10px;
    padding-bottom: 12px;
  }
  .user-menu-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .user-menu-options .user-menu-option {
    text-decoration: none;
    color: var(--base-black);
    border-radius: 4px;
    padding: 4px 6px;
    font-size: var(--hr-font-size-sm);
  }
  .user-menu-options .user-menu-option:hover {
    background-color: var(--primary-50);
  }

  .kaizen-top-bar {
    background-color: var(--secondary-25);
  }
</style>
